import { QUOTE_REQUESTS_MQ } from "@components/home/routes";
import { RAQ_FORM_TYPES, TAG_TYPES } from "@components/utils/constants";
import getReferralType from "@components/utils/getReferralType";
import { ITag } from "@modules/requestAQuote/components/TripPreferences";
import { getCookie } from "cookies-next";
import dayjs from "dayjs";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import useSWR from "swr";
import { ISearchQuery } from "../types/filtersProvider";
import {
  IAccommodationResponse,
  IActivityResponse,
  IFilteringEntryResponse,
  ILocationResponse,
  IPackageResponse,
} from "../types/modelResponses";
import useAppContext from "./useAppContext";

export interface IUseRequestQuoteReturn {
  isRequestOpen: boolean;
  isStepTwoOpen: boolean;
  isStepThreeOpen: boolean;
  isStepSuccessMobile: boolean;
  onRequestOpen: (isOpen: boolean) => void;
  onStepOneClick: (isOpen: boolean) => void;
  onStepTwoClick: (isOpen: boolean, isSuccessMobile?: boolean) => void;
  onStepThreeClick: (isOpen: boolean) => void;
  onRequestSubmit: (data: IRequestQuoteData) => Promise<void>;
  syncRaQFields: (
    accomsSetter: Dispatch<React.SetStateAction<ITag[]>>,
    activitiesSetter: Dispatch<React.SetStateAction<ITag[]>>,
    importantTagsSetter: Dispatch<React.SetStateAction<ITag[]>>
  ) => void;
  initializeRequestQuote: (
    accommodationsToStay: ITag[],
    autoPlaces: ITag[],
    importantTags: ITag[],
    locationData?:
      | IAccommodationResponse
      | ILocationResponse
      | IActivityResponse
      | IPackageResponse
  ) => void;
  requestSucceeded: boolean;
}

export interface IRequestQuoteData {
  fullName: string;
  phoneNumber: string;
  email: string;
  formType: string;
  dates: { startDate: string; endDate: string };
  travellers: {
    adults: number;
    teens: number;
    children: number;
    rooms: number;
  };
  budgets: {
    min: number;
    max: number;
  };
  locations: IFilteringEntryResponse[];
  accommodations: IFilteringEntryResponse[];
  activities: IFilteringEntryResponse[];
  importantTags: IFilteringEntryResponse[];
  description: string;
}

interface IFilteringEntryWithType extends IFilteringEntryResponse {
  type?: string;
}

interface IFilteringEntryIsChecked extends IFilteringEntryResponse {
  isChecked?: boolean;
}

const fetcher = async (
  payload: any
): Promise<
  (
    input: RequestInfo | URL,
    init?: RequestInit | undefined
  ) => Promise<Response>
> => {
  const options = {
    method: payload ? "POST" : "GET",
    ...(payload && {
      body: typeof payload === "string" ? payload : JSON.stringify(payload),
    }),
    headers: {
      "Content-Type": "application/json",
    },
  };
  // eslint-disable-next-line @typescript-eslint/return-await, @typescript-eslint/promise-function-async
  return fetch(QUOTE_REQUESTS_MQ, options).then((res) => res.json());
};
const useRequestQuote = (): IUseRequestQuoteReturn => {
  const appContext = useAppContext();
  const [isRequestOpen, setRequestOpen] = useState(false);
  const [isStepTwoOpen, setStepTwoOpen] = useState(false);
  const [isStepThreeOpen, setStepThreeOpen] = useState(false);
  const [isStepSuccessMobile, setStepSuccessMobile] = useState(false);

  const setShowToastMessage = appContext?.setRaqToast;

  const [requestQuoteData, setRequestQuoteData] = useState({});

  const [requestSucceeded, setRequestSucceeded] = useState(false);

  useSWR(
    [requestQuoteData],
    // @ts-expect-error
    Object.keys(requestQuoteData).length > 0 && fetcher,
    {
      onSuccess: () => {
        setRequestQuoteData({});
        setRequestSucceeded(true);
      },
      onError: (err) => {
        setRequestQuoteData({});
        if (setShowToastMessage) {
          setShowToastMessage(true);
          setTimeout(() => {
            setShowToastMessage(false);
          }, 3000);
        }
      },
    }
  );

  const onRequestOpen = useCallback(
    (isOpen: boolean) => {
      setRequestOpen(isOpen);
      setStepTwoOpen(false);
      setStepThreeOpen(false);
      if (appContext.isMobile) setStepSuccessMobile(false);
    },
    [setRequestOpen]
  );

  const onStepOneClick = useCallback(
    (isOpen: boolean) => {
      setStepTwoOpen(isOpen);
    },
    [setStepTwoOpen]
  );
  const onStepTwoClick = useCallback(
    (isOpen: boolean, isSuccessMobile = false) => {
      setStepTwoOpen(false);
      setStepThreeOpen(isOpen);
      if (appContext.isMobile && isSuccessMobile) {
        setStepTwoOpen(false);
        setStepThreeOpen(false);
        setStepSuccessMobile(true);
      }
    },
    [appContext.isMobile, setStepThreeOpen]
  );
  const onStepThreeClick = useCallback((isOpen: boolean) => {
    setStepThreeOpen(false);
    setStepTwoOpen(isOpen);
  }, []);

  const onRequestSubmit = useCallback(
    async (data: IRequestQuoteData) => {
      const { fullName, phoneNumber, email } = data;
      let currentRequestQuote;
      if (typeof window !== "undefined") {
        currentRequestQuote = JSON.parse(
          localStorage.getItem("USER_REQUEST_QUOTE") || "{}"
        );
      }

      const startDate =
        data.formType === RAQ_FORM_TYPES.FORM_B
          ? data?.dates?.startDate || ""
          : JSON.parse(
              sessionStorage.getItem("USER_QUOTE_DESKTOP_DATES") || "{}"
            )?.startDate ||
            JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE") || "{}")
              ?.startDate ||
            data?.dates?.startDate ||
            "";
      const endDate =
        data.formType === RAQ_FORM_TYPES.FORM_B
          ? data?.dates?.endDate || ""
          : JSON.parse(
              sessionStorage.getItem("USER_QUOTE_DESKTOP_DATES") || "{}"
            )?.endDate ||
            JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE") || "{}")
              ?.endDate ||
            data?.dates?.endDate ||
            "";
      const adults =
        currentRequestQuote?.travelers?.adults ||
        data?.travellers?.adults ||
        "";
      const children =
        currentRequestQuote?.travelers?.children ||
        data?.travellers?.children ||
        "";
      const teens =
        currentRequestQuote?.travelers?.teens || data?.travellers?.teens || "";
      const rooms =
        currentRequestQuote?.travelers?.rooms || data?.travellers?.rooms || "";
      const minBudget =
        currentRequestQuote?.budgets?.min || data?.budgets?.min || "";
      const maxBudget =
        currentRequestQuote?.budgets?.max || data?.budgets?.max || "";
      const locations =
        currentRequestQuote?.locations ||
        data?.locations.map((loc) => loc.title) ||
        [];
      const accommodations =
        currentRequestQuote?.accommodations ||
        data?.accommodations
          ?.filter((accom: IFilteringEntryIsChecked) => !accom.isChecked)
          .map((accom) => accom.title) ||
        [];
      const activities =
        currentRequestQuote?.activities ||
        data?.activities
          ?.filter((activity: IFilteringEntryIsChecked) => !activity.isChecked)
          .map((activity) => activity.title) ||
        [];
      const importantTags =
        currentRequestQuote?.importantTags || data?.importantTags || [];

      const experiences =
        data.formType === RAQ_FORM_TYPES.FORM_B
          ? importantTags
              .filter(
                (tag: IFilteringEntryWithType) => tag.type === TAG_TYPES.TAG
              )
              .map((tag: IFilteringEntryWithType) => tag.title)
              .join(", ")
          : undefined;

      const locationTypes =
        data.formType === RAQ_FORM_TYPES.FORM_B
          ? importantTags
              .filter(
                (tag: IFilteringEntryWithType) =>
                  tag.type === TAG_TYPES.LOCATION_TYPE
              )
              .map((tag: IFilteringEntryWithType) => tag.title)
              .join(", ")
          : undefined;

      const accommodationTypes =
        data?.accommodations
          ?.filter(
            (accom: IFilteringEntryIsChecked) => accom.isChecked === true
          )
          .map((accom) => accom.title) || [];

      const activityTypes =
        data?.activities
          ?.filter(
            (activity: IFilteringEntryWithType) =>
              activity.type === TAG_TYPES.ACTIVITY_TYPE
          )
          .map((activity) => activity.title) || [];

      const requestData = {
        Email: email, // Email Address
        MobilePhone: phoneNumber, // Phone number
        UsrAdultCount: adults, // Adult count
        UsrChildCount: children, // Child Count
        UsrTeens: teens, // Teens count
        UsrRooms: rooms, // Rooms count
        UsrArrivalDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "", // Arrival date
        UsrDepartureDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "", // Departure date
        UsrComments:
          data.formType === RAQ_FORM_TYPES.FORM_B
            ? `${data.description + "\n"}`
            : importantTags.join(", "), // User comments
        Contact: fullName, // Full Name
        Locations: locations.join(", "), // List of locations
        Lodging: accommodations.join(", "), // List of accommodations and property type
        Activities: activities.join(", "), // List of activities and  location types
        Min_Rate_Per_Person_Per_Night: minBudget, // Min nightly budget
        Max_Rate_Per_Person_Per_Night: maxBudget, // Max nightly budget
        PageURL: window.location.href,
        UsrExpTags: experiences, // List of experiences
        UsrLocationType: locationTypes, // List of location types
        UsrLodgingType: accommodationTypes.join(", "), // List of lodging types
        UsrActivityType: activityTypes.join(", "), // List of activity types
      };

      // @ts-expect-error
      requestData.UsrReferralType = getReferralType();
      // @ts-expect-error
      requestData.UsrSourceValue = getCookie("roasurl");

      setRequestQuoteData(JSON.stringify(requestData));
    },

    [appContext.isMobile]
  );

  useEffect(() => {
    if (!isRequestOpen && isStepSuccessMobile) {
      setStepSuccessMobile(false);
    }
  }, [isRequestOpen, isStepSuccessMobile]);

  const syncRaQFields = useCallback(
    (
      accomsSetter: Dispatch<SetStateAction<ITag[]>>,
      activitiesSetter: Dispatch<SetStateAction<ITag[]>>,
      importantTagsSetter: Dispatch<SetStateAction<ITag[]>>
    ) => {
      const syncFields = (
        currentSearchQuery: ISearchQuery,
        likedAccommodations: any[],
        likedActivities: any[]
      ): void => {
        if (currentSearchQuery !== null) {
          const accommodations = currentSearchQuery?.accommodations;
          const hotels = accommodations?.hotels;
          const rentals = accommodations?.rentals;
          const accommodationCategories = [
            {
              id: "hotel",
              title: accommodations?.hotels.title,
              categoryName: "hotels",
              propertyCategory: null,
              isChecked: accommodations?.hotels.isChecked,
            },
            {
              id: "rental",
              title: accommodations?.rentals.title,
              categoryName: "rentals",
              propertyCategory: null,
              isChecked: accommodations?.rentals.isChecked,
            },
          ];

          const experiences = currentSearchQuery?.experiences;
          const locationTypes = currentSearchQuery?.locationTypes;
          activitiesSetter(
            likedActivities
              ? [
                  ...currentSearchQuery?.activityTypes?.filter(
                    (activityType) => activityType.isChecked
                  ),
                  ...likedActivities,
                ].filter(
                  (value, index, self) =>
                    index === self.findIndex((t) => t.id === value.id)
                )
              : [
                  ...currentSearchQuery?.activityTypes?.filter(
                    (activityType) => activityType.isChecked
                  ),
                ].filter(
                  (value, index, self) =>
                    index === self.findIndex((t) => t.id === value.id)
                )
          );
          accomsSetter(
            likedAccommodations
              ? [
                  ...accommodationCategories.filter(
                    (category) => category.isChecked
                  ),
                  ...hotels?.categories?.filter((hotel) => hotel.isChecked),
                  ...rentals?.categories?.filter((rental) => rental.isChecked),
                  ...likedAccommodations,
                ].filter(
                  (value, index, self) =>
                    index === self.findIndex((t) => t.id === value.id)
                )
              : [
                  ...accommodationCategories.filter(
                    (category) => category.isChecked
                  ),
                  ...hotels?.categories?.filter((hotel) => hotel.isChecked),
                  ...rentals?.categories?.filter((rental) => rental.isChecked),
                ].filter(
                  (value, index, self) =>
                    index === self.findIndex((t) => t.id === value.id)
                )
          );
          importantTagsSetter(() => {
            if (experiences && locationTypes) {
              return [...experiences, ...locationTypes].filter(
                (importantItem) => importantItem.isChecked
              );
            } else if (experiences) {
              return [...experiences].filter(
                (importantItem) => importantItem.isChecked
              );
            } else if (locationTypes) {
              return [...locationTypes].filter(
                (importantItem) => importantItem.isChecked
              );
            } else {
              return [];
            }
          });
        } else {
          activitiesSetter(likedActivities ? [...likedActivities] : []);
          accomsSetter(likedAccommodations ? [...likedAccommodations] : []);
        }
      };

      useEffect(() => {
        const onStorageEvent = (): void => {
          const currentSearchQuery = JSON.parse(
            // @ts-expect-error
            localStorage.getItem("USER_SEARCH_FILTERS")
          );
          const likedAccommodations = JSON.parse(
            localStorage.getItem("ACC_LIKES") || "[]"
          );
          const likedActivities = JSON.parse(
            localStorage.getItem("ACT_LIKES") || "[]"
          );

          syncFields(currentSearchQuery, likedAccommodations, likedActivities);
        };

        onStorageEvent();

        window.addEventListener("storage", onStorageEvent);

        return () => window.removeEventListener("storage", onStorageEvent);
      }, []);
    },
    []
  );

  const initializeRequestQuote = useCallback(
    (
      accommodationsToStay: ITag[],
      autoPlaces: ITag[],
      importantTags: ITag[],
      locationData: any
    ) => {
      localStorage.setItem(
        "USER_REQUEST_QUOTE",
        JSON.stringify({
          startDate: JSON.parse(
            localStorage.getItem("USER_SEARCH_QUERY_PARAMS") || "{}"
          )?.startDate
            ? JSON.parse(
                localStorage.getItem("USER_SEARCH_QUERY_PARAMS") || "{}"
              ).startDate
            : null,
          endDate: JSON.parse(
            localStorage.getItem("USER_SEARCH_QUERY_PARAMS") || "{}"
          )?.endDate
            ? JSON.parse(
                localStorage.getItem("USER_SEARCH_QUERY_PARAMS") || "{}"
              ).endDate
            : null,
          budgets: {
            min: localStorage.getItem("USER_SEARCH_FILTERS")
              ? JSON.parse(localStorage.getItem("USER_SEARCH_FILTERS") || "{}")
                  .accommodations.budget.min
              : null,
            max: localStorage.getItem("USER_SEARCH_FILTERS")
              ? JSON.parse(localStorage.getItem("USER_SEARCH_FILTERS") || "{}")
                  .accommodations.budget.max
              : null,
          },
          travelers: {
            adults: 2,
            teens: 0,
            children: 0,
          },
          locations: locationData?.locations
            ? locationData.locations.map(
                (location: { Title: string }) => location.Title
              )
            : [locationData.Title],
          accommodations: accommodationsToStay.map((accom) => accom.title),
          activities: autoPlaces.map((activity) => activity.title),
          importantTags: importantTags.map((tag) => tag.title),
        })
      );
    },
    []
  );

  return {
    isRequestOpen,
    isStepTwoOpen,
    isStepThreeOpen,
    isStepSuccessMobile,
    onRequestOpen,
    onStepOneClick,
    onStepTwoClick,
    onStepThreeClick,
    onRequestSubmit,
    syncRaQFields,
    initializeRequestQuote,
    requestSucceeded,
  };
};

export default useRequestQuote;
